// © Microsoft Corporation. All rights reserved.

import { PerfTimeline, tryGetPerfTimeline } from "@msnews/diagnostics";
import { dispatchAction, getActionPayload } from "./ActionDispatchHelper";

import { ActionSenderBase } from "./ActionSenderBase";
import { IActionPayload } from "@msnews/experiences-redux-contracts";
import { Observer } from "./ActionObserver";

/**
 * Class that wraps the sending of a action globally to all connectors
 * @export
 * @class
 */
export class GlobalActionSender<TActionSignature extends Function> extends ActionSenderBase<TActionSignature> {

    /**
     * Creates an instance of the `GlobalActionSender` class.
     * @constructor
     * @param {string} type The action type.
     * @param {string} experienceInstanceId Optional experienceInstanceId of the experience that is originating the action.
     */
    constructor(
        type: string,
        observers: Observer[],
        private experienceInstanceId?: string) {

        super(type, observers, null);
    }

    /**
     * Core dispatch implementation for {GlobalActionSender}
     * @param args - the arguments being dispatched
     * @returns dispatch result metadata
     */
    protected getDispatchResult(args: any[]): { payload: IActionPayload, result: any } {
        const payload = getActionPayload(undefined, this.type, args, this.experienceInstanceId);
        const result = dispatchAction(undefined, payload);
        return { payload, result };
    }

    /**
     * Gets the perf timeline if applicable
     */
    protected tryGetPerfTimeline(): PerfTimeline {
        return tryGetPerfTimeline("GlobalActionSender", this.experienceInstanceId || "unknown");
    }
}